/**
 * AnexosManager - Componente para gerenciar anexos de documentos
 * 
 * Suporta: PDF, Word, Excel, Imagens (JPEG, PNG), e outros formatos
 * Permite: Upload, visualização, renomeação e exclusão
 */
import React, { useState, useRef } from 'react';
import axios from 'axios';
import api from '../lib/axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { 
  Paperclip, Upload, Trash2, Download, Eye, Edit2, 
  FileText, FileSpreadsheet, FileImage, File, X, Check,
  Loader2, ExternalLink, Info
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// Formatar data/hora em pt-BR (DD/MM/YYYY HH:MM)
const formatUploadedAt = (iso) => {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return null;
    return d.toLocaleString('pt-BR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  } catch {
    return null;
  }
};

// Verifica se anexo pode ser previewado inline
const canPreview = (tipo, nome) => {
  const ext = nome?.split('.').pop()?.toLowerCase() || '';
  if (tipo?.includes('pdf') || ext === 'pdf') return 'pdf';
  if (tipo?.includes('image') || ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)) return 'image';
  return null;
};

// Ícones por tipo de arquivo
const getFileIcon = (tipo, nome) => {
  const ext = nome?.split('.').pop()?.toLowerCase() || '';
  
  if (tipo?.includes('pdf') || ext === 'pdf') {
    return <FileText className="h-5 w-5 text-red-500" />;
  }
  if (tipo?.includes('word') || tipo?.includes('document') || ['doc', 'docx'].includes(ext)) {
    return <FileText className="h-5 w-5 text-blue-500" />;
  }
  if (tipo?.includes('excel') || tipo?.includes('spreadsheet') || ['xls', 'xlsx', 'csv'].includes(ext)) {
    return <FileSpreadsheet className="h-5 w-5 text-green-500" />;
  }
  if (tipo?.includes('image') || ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) {
    return <FileImage className="h-5 w-5 text-purple-500" />;
  }
  return <File className="h-5 w-5 text-gray-500" />;
};

// Formatar tamanho do arquivo
const formatFileSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// Verificar se é uma imagem
const isImage = (tipo, nome) => {
  const ext = nome?.split('.').pop()?.toLowerCase() || '';
  return tipo?.includes('image') || ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext);
};

const AnexosManager = ({ 
  anexos = [], 
  onChange, 
  maxFiles = 20,
  maxSizeMB = 50,
  label = "Anexos",
  showLabel = true,
  modulo = null,
  entidadeId = null,
  subEntidadeId = null,
  categoria = null,
  uploadHandler = null,
  deleteHandler = null,
  allowDescription = false,
  metadataUpdateHandler = null,
  // Fase: anexos vinculados a item da cotação
  itensCotacao = null,         // Array de { id, descricao } — se fornecido, habilita vínculo a item
  vincularItemHandler = null,  // async (anexo, item_id) => void  (para vincular anexos existentes)
}) => {
  // Defensivo: garantir que anexos é sempre array E que cada item é objeto válido normalizado
  const asString = (v) => {
    if (v == null) return '';
    if (typeof v === 'string') return v;
    if (typeof v === 'number' || typeof v === 'boolean') return String(v);
    try { return JSON.stringify(v); } catch { return ''; }
  };
  const asNumber = (v, def = 0) => {
    const n = typeof v === 'number' ? v : parseFloat(v);
    return Number.isFinite(n) ? n : def;
  };
  const normalizeAnexo = (a, idx) => {
    if (!a || typeof a !== 'object') return null;
    return {
      ...a,
      id: asString(a.id) || `item-${idx}`,
      nome: asString(a.nome || a.filename || a.name) || `Arquivo ${idx + 1}`,
      nome_exibicao: asString(a.nome_exibicao || ''),
      descricao: asString(a.descricao || ''),
      url: asString(a.url || a.download_url || ''),
      tipo: asString(a.tipo || a.content_type || a.mimetype || ''),
      tamanho: asNumber(a.tamanho || a.size || a.tamanho_bytes, 0),
      data_upload: asString(a.data_upload || a.uploaded_at || a.criado_em || ''),
      item_id: asString(a.item_id || ''),
    };
  };
  const safeAnexos = (Array.isArray(anexos) ? anexos : [])
    .map(normalizeAnexo)
    .filter(Boolean);
  const [uploading, setUploading] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editName, setEditName] = useState('');
  const [editingDescIndex, setEditingDescIndex] = useState(null);
  const [editDesc, setEditDesc] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [previewAnexo, setPreviewAnexo] = useState(null);
  const [reuploadIndex, setReuploadIndex] = useState(null);
  const fileInputRef = useRef(null);
  const reuploadInputRef = useRef(null);
  const dropZoneRef = useRef(null);

  // Vínculo a item da cotação (Fase: anexos por item no PDF)
  const itensList = Array.isArray(itensCotacao) ? itensCotacao.filter(i => i?.id) : [];
  const showItemBinding = itensList.length > 0;
  const [pendingItemId, setPendingItemId] = useState('');  // selecionado ANTES do upload

  // Trigger do reupload (clicar em "Reenvie este arquivo")
  const triggerReupload = (index) => {
    setReuploadIndex(index);
    if (reuploadInputRef.current) {
      reuploadInputRef.current.value = '';
      reuploadInputRef.current.click();
    }
  };

  // Quando o usuário seleciona o novo arquivo
  const handleReuploadInputChange = async (event) => {
    const file = event.target.files?.[0];
    const index = reuploadIndex;
    setReuploadIndex(null);
    if (file && index !== null && index !== undefined) {
      await handleReuploadFile(index, file);
    }
    if (reuploadInputRef.current) {
      reuploadInputRef.current.value = '';
    }
  };

  const getAuthHeader = () => {
    const token = localStorage.getItem('nautilus_token');
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  // Upload de um único arquivo (retorna o objeto anexo ou null)
  const uploadSingleFile = async (file) => {
    if (file.size > maxSizeMB * 1024 * 1024) {
      toast.error(`${file.name}: Arquivo muito grande (máx ${maxSizeMB}MB)`);
      return null;
    }
    try {
      // Handler customizado (usado por integrações específicas)
      if (uploadHandler) {
        return await uploadHandler(file, pendingItemId || null);
      }

      const formData = new FormData();
      formData.append('file', file);
      const token = localStorage.getItem('nautilus_token');

      let uploadUrl = `${API}/api/upload/anexo`;
      const params = [];
      if (modulo) params.push(`modulo=${encodeURIComponent(modulo)}`);
      if (entidadeId) params.push(`entidade_id=${encodeURIComponent(entidadeId)}`);
      if (subEntidadeId) params.push(`sub_entidade_id=${encodeURIComponent(subEntidadeId)}`);
      if (categoria) params.push(`categoria=${encodeURIComponent(categoria)}`);
      if (params.length) uploadUrl += `?${params.join('&')}`;

      const response = await axios.post(uploadUrl, formData, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.data && response.data.url) {
        return {
          id: response.data.filename || Date.now().toString(),
          nome: file.name,
          nome_exibicao: file.name.replace(/\.[^/.]+$/, ''),
          url: response.data.url,
          tipo: file.type,
          tamanho: file.size,
          data_upload: new Date().toISOString(),
          s3_key: response.data.s3_key || null,
          uploaded_at: response.data.uploaded_at || new Date().toISOString(),
          uploaded_by: response.data.uploaded_by || null,
        };
      }
      return null;
    } catch (err) {
      console.error('Erro no upload:', err);
      const detail = err?.response?.data?.detail || err?.message || 'Erro ao enviar';
      toast.error(`${file.name}: ${detail}`);
      return null;
    }
  };

  // Reupload: substitui um anexo perdido pelo novo arquivo (mantém posição)
  const handleReuploadFile = async (index, file) => {
    if (!file) return;
    setUploading(true);
    try {
      const novoAnexo = await uploadSingleFile(file);
      if (novoAnexo) {
        const novosAnexos = [...safeAnexos];
        novosAnexos[index] = novoAnexo; // substitui no MESMO índice
        onChange(novosAnexos);
        toast.success(`Arquivo reenviado com sucesso!`);
      }
    } finally {
      setUploading(false);
    }
  };

  // Processar arquivos (usado tanto pelo input quanto pelo drag and drop)
  const processFiles = async (files) => {
    const fileList = Array.from(files);
    if (!fileList.length) return;

    if (safeAnexos.length + fileList.length > maxFiles) {
      toast.error(`Máximo de ${maxFiles} anexos permitidos`);
      return;
    }

    setUploading(true);
    const novosAnexos = [...safeAnexos];

    for (const file of fileList) {
      const novoAnexo = await uploadSingleFile(file);
      if (novoAnexo) {
        novosAnexos.push(novoAnexo);
        toast.success(`${file.name} anexado com sucesso`);
      }
    }

    onChange(novosAnexos);
    setUploading(false);
  };

  // Upload de arquivo via input
  const handleFileUpload = async (event) => {
    await processFiles(event.target.files);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Handlers de Drag and Drop
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Verificar se realmente saiu da área de drop
    if (dropZoneRef.current && !dropZoneRef.current.contains(e.relatedTarget)) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      await processFiles(files);
    }
  };

  // Remover anexo
  const handleRemove = async (index) => {
    const anexo = safeAnexos[index];
    if (!anexo) return;
    if (deleteHandler) {
      try {
        await deleteHandler(anexo);
      } catch (e) {
        console.error('Erro ao remover anexo:', e);
        toast.error('Erro ao remover anexo');
        return;
      }
    }
    const novosAnexos = safeAnexos.filter((_, i) => i !== index);
    onChange(novosAnexos);
    toast.info('Anexo removido');
  };

  // Iniciar edição do nome
  const startEdit = (index) => {
    const a = safeAnexos[index];
    if (!a) return;
    setEditingIndex(index);
    setEditName(a.nome_exibicao || (a.nome || '').replace(/\.[^/.]+$/, ''));
  };

  // Salvar nome editado
  const saveEdit = async () => {
    if (editingIndex === null) return;
    
    const novosAnexos = [...safeAnexos];
    const ext = novosAnexos[editingIndex].nome.split('.').pop();
    novosAnexos[editingIndex] = {
      ...novosAnexos[editingIndex],
      nome_exibicao: editName,
      nome: `${editName}.${ext}`
    };
    
    try {
      if (metadataUpdateHandler) {
        await metadataUpdateHandler(novosAnexos[editingIndex], { nome_exibicao: editName });
      }
      onChange(novosAnexos);
      toast.success('Nome atualizado');
    } catch (err) {
      toast.error('Erro ao salvar nome');
    } finally {
      setEditingIndex(null);
      setEditName('');
    }
  };

  // Cancelar edição
  const cancelEdit = () => {
    setEditingIndex(null);
    setEditName('');
  };

  // Iniciar edição da descrição
  const startEditDesc = (index) => {
    const a = safeAnexos[index];
    if (!a) return;
    setEditingDescIndex(index);
    setEditDesc(a.descricao || '');
  };

  // Salvar descrição editada
  const saveEditDesc = async () => {
    if (editingDescIndex === null) return;
    const idx = editingDescIndex;
    const novosAnexos = [...safeAnexos];
    const novaDesc = (editDesc || '').trim();
    novosAnexos[idx] = { ...novosAnexos[idx], descricao: novaDesc };

    try {
      if (metadataUpdateHandler) {
        await metadataUpdateHandler(novosAnexos[idx], { descricao: novaDesc });
      }
      onChange(novosAnexos);
      toast.success('Descrição atualizada');
    } catch (err) {
      toast.error('Erro ao salvar descrição');
    } finally {
      setEditingDescIndex(null);
      setEditDesc('');
    }
  };

  const cancelEditDesc = () => {
    setEditingDescIndex(null);
    setEditDesc('');
  };

  // Abrir arquivo - SEMPRE via proxy do backend (URLs S3 pré-assinadas expiram)
  const getStableUrl = (anexo) => {
    const url = anexo.url || '';
    
    // Blob URL local (arquivo pendente, nao salvo) - manter como esta
    if (url.startsWith('blob:') || url.startsWith('data:')) return url;
    
    // Se já é URL proxy do backend (relativa)
    if (url.startsWith('/api/')) return url;
    
    // Se é URL S3 (pré-assinada ou direta) - converter para proxy estável
    if (url.includes('amazonaws.com') || url.includes('s3.') || url.startsWith('s3://')) {
      // Extrair o filename da URL S3 (última parte do path, sem query params)
      const urlPath = url.split('?')[0];
      const filename = anexo.id || urlPath.split('/').pop();
      if (filename) return `/api/anexos/${filename}`;
    }
    
    // Se é URL absoluta com nosso domínio + /api/
    if (url.includes('/api/anexos/') || url.includes('/api/images/')) {
      const match = url.match(/\/api\/(anexos|images)\/(.+)/);
      if (match) return `/api/${match[1]}/${match[2]}`;
    }
    
    // Se é URL completa externa (CDN, etc) - SEM S3
    if (url.startsWith('http')) return url;
    
    // Fallback: construir URL proxy a partir do id ou url
    const filename = anexo.id || url;
    return `/api/anexos/${filename}`;
  };

  const openFile = (anexo) => {
    const url = getStableUrl(anexo);
    window.open(url, '_blank');
  };

  const handlePreview = (anexo) => {
    if (canPreview(anexo.tipo, anexo.nome)) {
      setPreviewAnexo(anexo);
    } else {
      openFile(anexo);
    }
  };

  return (
    <div className="space-y-3">
      {showLabel && (
        <div className="flex items-center justify-between">
          <Label className="text-sm font-medium flex items-center gap-2">
            <Paperclip className="h-4 w-4" />
            {label}
          </Label>
          <span className="text-xs text-gray-500">
            {safeAnexos.length}/{maxFiles} arquivos
          </span>
        </div>
      )}

      {/* Vínculo a item da cotação (Fase: anexos por item no PDF) */}
      {showItemBinding && (
        <div className="flex items-center gap-2 mb-2 p-2 rounded-md bg-amber-50/60 border border-amber-200" data-testid="anexos-vincular-item">
          <span className="text-xs text-amber-800 shrink-0">📌 Vincular novo anexo a:</span>
          <select
            value={pendingItemId}
            onChange={(e) => setPendingItemId(e.target.value)}
            className="text-xs border border-amber-300 rounded px-2 py-1 bg-white flex-1 min-w-0"
            data-testid="select-anexo-item"
          >
            <option value="">🌐 Anexo geral da cotação</option>
            {itensList.map((it, idx) => (
              <option key={it.id} value={it.id}>
                Item {idx + 1} — {(it.descricao || '').substring(0, 80)}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Área de Upload com Drag and Drop */}
      <div 
        ref={dropZoneRef}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        className={`relative border-2 border-dashed rounded-lg transition-all
          ${isDragging 
            ? 'border-blue-500 bg-blue-50 scale-[1.02]' 
            : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50'
          }
          ${uploading ? 'opacity-50 pointer-events-none' : ''}
        `}
      >
        <label htmlFor={`anexos-upload-${label}`} className="block cursor-pointer p-6">
          <input
            ref={fileInputRef}
            id={`anexos-upload-${label}`}
            type="file"
            multiple
            accept="image/*,.heic,.heif,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.csv,.txt,.zip,.rar,.7z,.gif,.webp,.dwg,.dxf,.stp,.step,.igs,.iges,.stl,.3dm,.sldprt,.sldasm,.ipt,.iam,.prt,.x_t,.x_b,.sat"
            onChange={handleFileUpload}
            className="sr-only"
          />
          {/* Input invisível usado para reupload de anexos perdidos (1 arquivo por vez) */}
          <input
            ref={reuploadInputRef}
            type="file"
            accept="image/*,.heic,.heif,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.csv,.txt,.zip,.rar,.7z,.gif,.webp,.dwg,.dxf,.stp,.step,.igs,.iges,.stl,.3dm,.sldprt,.sldasm,.ipt,.iam,.prt,.x_t,.x_b,.sat"
            onChange={handleReuploadInputChange}
            className="sr-only"
            data-testid="anexos-reupload-input"
          />
          
          <div className="flex flex-col items-center justify-center gap-3">
            {uploading ? (
              <>
                <Loader2 className="h-10 w-10 text-blue-500 animate-spin" />
                <p className="text-sm font-medium text-gray-700">Enviando arquivos...</p>
              </>
            ) : isDragging ? (
              <>
                <Upload className="h-10 w-10 text-blue-500 animate-bounce" />
                <p className="text-sm font-medium text-blue-600">Solte os arquivos aqui!</p>
              </>
            ) : (
              <>
                <div className="p-3 bg-gray-100 rounded-full">
                  <Upload className="h-6 w-6 text-gray-500" />
                </div>
                <div className="text-center">
                  <p className="text-sm font-medium text-gray-700">
                    Toque para selecionar ou arraste arquivos aqui
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    PDF, Word, Excel, Imagens (máx {maxSizeMB}MB cada)
                  </p>
                </div>
              </>
            )}
          </div>
        </label>
      </div>

      {/* Lista de Anexos */}
      {safeAnexos.length > 0 && (
        <div className="space-y-2">
          {safeAnexos.map((anexo, index) => (
            <Card key={anexo.id || index} className={`p-3 ${anexo.perdido ? 'border-red-200 bg-red-50/50' : ''}`} data-testid={`anexo-card-${index}`}>
              <div className="flex items-center gap-3">
                {/* Ícone do tipo de arquivo (clicável p/ preview) */}
                <button
                  type="button"
                  onClick={() => handlePreview(anexo)}
                  className="shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
                  title={canPreview(anexo.tipo, anexo.nome) ? 'Visualizar' : 'Abrir em nova aba'}
                  data-testid={`anexo-thumb-${index}`}
                >
                  {isImage(anexo.tipo, anexo.nome) ? (
                    <div className="w-10 h-10 rounded overflow-hidden border bg-gray-50">
                      <img 
                        src={getStableUrl(anexo)} 
                        alt={anexo.nome}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.parentElement.innerHTML = '<div class="w-full h-full flex items-center justify-center"><svg class="h-5 w-5 text-purple-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg></div>';
                        }}
                      />
                    </div>
                  ) : (
                    <div className="w-10 h-10 rounded border bg-gray-50 flex items-center justify-center">
                      {getFileIcon(anexo.tipo, anexo.nome)}
                    </div>
                  )}
                </button>

                {/* Nome do arquivo */}
                <div className="flex-1 min-w-0">
                  {editingIndex === index ? (
                    <div className="flex items-center gap-2">
                      <Input
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        className="h-8 text-sm"
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') saveEdit();
                          if (e.key === 'Escape') cancelEdit();
                        }}
                      />
                      <Button type="button" size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={saveEdit}>
                        <Check className="h-4 w-4 text-green-600" />
                      </Button>
                      <Button type="button" size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={cancelEdit}>
                        <X className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  ) : (
                    <>
                      <p className="text-sm font-medium truncate" title={anexo.nome_exibicao || anexo.nome}>
                        {anexo.nome_exibicao || anexo.nome}
                      </p>
                      {/* Vínculo a item da cotação (badge + popover de vincular depois) */}
                      {showItemBinding && (
                        <div className="mt-0.5 flex items-center gap-1 flex-wrap">
                          {anexo.item_id ? (
                            <span
                              className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-orange-50 border border-orange-200 text-orange-700"
                              data-testid={`anexo-vinculo-${index}`}
                              title={(() => {
                                const it = itensList.find(i => i.id === anexo.item_id);
                                return it ? `Vinculado ao item: ${it.descricao}` : 'Vinculado a item';
                              })()}
                            >
                              📌 {(() => {
                                const idxIt = itensList.findIndex(i => i.id === anexo.item_id);
                                if (idxIt < 0) return 'Item';
                                const it = itensList[idxIt];
                                const desc = (it.descricao || '').substring(0, 30);
                                return `Item ${idxIt + 1} — ${desc}${(it.descricao || '').length > 30 ? '...' : ''}`;
                              })()}
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-slate-100 border border-slate-200 text-slate-600">
                              🌐 Geral
                            </span>
                          )}
                          {vincularItemHandler && (
                            <select
                              className="text-[10px] border border-slate-200 rounded px-1 py-0.5 bg-white hover:border-blue-400 focus:outline-none"
                              value={anexo.item_id || ''}
                              onChange={async (e) => {
                                try {
                                  await vincularItemHandler(anexo, e.target.value || null);
                                  toast.success('Vínculo atualizado');
                                } catch (err) {
                                  toast.error('Erro ao vincular');
                                }
                              }}
                              data-testid={`select-vincular-${index}`}
                              title="Alterar vínculo do anexo"
                            >
                              <option value="">🌐 Geral</option>
                              {itensList.map((it, i) => (
                                <option key={it.id} value={it.id}>
                                  Item {i + 1} — {(it.descricao || '').substring(0, 40)}
                                </option>
                              ))}
                            </select>
                          )}
                        </div>
                      )}
                      {anexo.perdido && (
                        <button
                          type="button"
                          onClick={() => triggerReupload(index)}
                          className="mt-0.5 text-xs font-bold text-red-600 hover:text-red-800 flex items-center gap-1 cursor-pointer underline-offset-2 hover:underline"
                          data-testid={`anexo-perdido-badge-${index}`}
                          title="Clique para selecionar o arquivo correto e enviar novamente"
                        >
                          <span className="inline-block w-2 h-2 rounded-full bg-red-500 animate-pulse"></span>
                          Reenvie este arquivo
                        </button>
                      )}
                      <p className="text-xs text-gray-500">
                        {formatFileSize(anexo.tamanho)}
                        {anexo.nome.includes('.') && (
                          <span className="ml-2 uppercase">
                            {anexo.nome.split('.').pop()}
                          </span>
                        )}
                      </p>
                      {/* Metadata: data/hora do upload + responsável + ID S3 */}
                      {(anexo.uploaded_at || anexo.uploaded_by || anexo.s3_key) && (
                        <div className="flex items-center gap-1 mt-0.5 text-[10px] text-gray-400" data-testid={`anexo-metadata-${index}`}>
                          {formatUploadedAt(anexo.uploaded_at) && (
                            <span className="truncate">
                              {formatUploadedAt(anexo.uploaded_at)}
                              {anexo.uploaded_by && ` · por ${anexo.uploaded_by}`}
                            </span>
                          )}
                          {anexo.s3_key && (
                            <span
                              className="inline-flex items-center justify-center cursor-help text-gray-400 hover:text-blue-500 ml-1 shrink-0"
                              title={`Identificação do arquivo no S3 Amazon:\n${anexo.s3_key}`}
                              data-testid={`anexo-s3-info-${index}`}
                            >
                              <Info className="h-3 w-3" />
                            </span>
                          )}
                        </div>
                      )}
                      {/* Descrição (Fase 2) — editável inline quando allowDescription */}
                      {allowDescription && (
                        <div className="mt-1" data-testid={`anexo-desc-${index}`}>
                          {editingDescIndex === index ? (
                            <div className="flex items-start gap-2">
                              <Input
                                value={editDesc}
                                onChange={(e) => setEditDesc(e.target.value)}
                                placeholder="Descrição (ex: Foto do motor atual)"
                                className="h-7 text-xs"
                                autoFocus
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') saveEditDesc();
                                  if (e.key === 'Escape') cancelEditDesc();
                                }}
                                data-testid={`anexo-desc-input-${index}`}
                              />
                              <Button type="button" size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={saveEditDesc}>
                                <Check className="h-4 w-4 text-green-600" />
                              </Button>
                              <Button type="button" size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={cancelEditDesc}>
                                <X className="h-4 w-4 text-red-500" />
                              </Button>
                            </div>
                          ) : (
                            <button
                              type="button"
                              onClick={() => startEditDesc(index)}
                              className="text-xs text-gray-600 italic hover:text-blue-600 transition-colors text-left w-full truncate"
                              title="Clique para editar a descrição"
                              data-testid={`anexo-desc-btn-${index}`}
                            >
                              {anexo.descricao
                                ? anexo.descricao
                                : <span className="text-gray-400">+ adicionar descrição</span>}
                            </button>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* Ações */}
                {editingIndex !== index && (
                  <div className="flex items-center gap-1 shrink-0">
                    {canPreview(anexo.tipo, anexo.nome) && (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="h-8 w-8 p-0"
                        onClick={() => handlePreview(anexo)}
                        title="Visualizar"
                        data-testid={`anexo-preview-${index}`}
                      >
                        <Eye className="h-4 w-4 text-blue-600" />
                      </Button>
                    )}
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="h-8 w-8 p-0"
                      onClick={() => startEdit(index)}
                      title="Renomear"
                      data-testid={`anexo-rename-${index}`}
                    >
                      <Edit2 className="h-4 w-4 text-gray-500" />
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="h-8 w-8 p-0"
                      onClick={() => openFile(anexo)}
                      title="Abrir em nova aba"
                      data-testid={`anexo-open-${index}`}
                    >
                      <ExternalLink className="h-4 w-4 text-blue-500" />
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="h-8 w-8 p-0"
                      onClick={() => handleRemove(index)}
                      title="Remover"
                      data-testid={`anexo-remove-${index}`}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Mensagem quando não há anexos */}
      {safeAnexos.length === 0 && (
        <p className="text-xs text-gray-400 text-center py-2">
          Nenhum anexo adicionado
        </p>
      )}

      {/* Modal de Preview (PDF / Imagem) */}
      <Dialog open={!!previewAnexo} onOpenChange={(open) => !open && setPreviewAnexo(null)}>
        <DialogContent
          className="max-w-5xl w-[95vw] h-[90vh] p-0 flex flex-col"
          data-testid="anexo-preview-dialog"
        >
          <DialogHeader className="px-4 py-3 border-b flex-row items-center justify-between space-y-0">
            <DialogTitle className="text-sm font-medium truncate pr-4">
              {previewAnexo?.nome_exibicao || previewAnexo?.nome}
            </DialogTitle>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => previewAnexo && openFile(previewAnexo)}
                data-testid="anexo-preview-open-new-tab"
              >
                <ExternalLink className="h-4 w-4 mr-1" />
                Nova aba
              </Button>
            </div>
          </DialogHeader>
          <div className="flex-1 bg-gray-100 overflow-hidden">
            {previewAnexo && canPreview(previewAnexo.tipo, previewAnexo.nome) === 'pdf' && (
              <iframe
                src={getStableUrl(previewAnexo)}
                className="w-full h-full border-0"
                title={previewAnexo.nome}
                data-testid="anexo-preview-pdf"
              />
            )}
            {previewAnexo && canPreview(previewAnexo.tipo, previewAnexo.nome) === 'image' && (
              <div className="w-full h-full flex items-center justify-center p-4 overflow-auto">
                <img
                  src={getStableUrl(previewAnexo)}
                  alt={previewAnexo.nome}
                  className="max-w-full max-h-full object-contain"
                  data-testid="anexo-preview-image"
                />
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AnexosManager;
